import { HTTP_STATUS, isApiError } from '@brightlocal/api-client';

import { isSessionExpiredError } from 'shared/services/auth/sessionExpiredError';
import { isNetworkApiError, type Feature, type SentryLevel } from 'shared/utils';

import { classifyReportCreateError, type ReportCreateErrorKind } from './classifyReportCreateError';
import { isReportCreateError } from './reportCreateError';
import type { ReportCreateTool } from '../consts';

const FINGERPRINT_PREFIX = 'report-create';

// Expected rejections are kept for counting only; gateway failures are worth a look; the rest is a defect.
const LEVEL_BY_KIND: Record<ReportCreateErrorKind, SentryLevel> = {
  entitlement: 'info',
  duplicate: 'info',
  no_source: 'info',
  gateway: 'warning',
  validation: 'error',
  unknown: 'error'
};

const TOOL_LABELS: Record<ReportCreateTool, string> = {
  lsg: 'LSG',
  ct: 'CT',
  rm: 'RM',
  lrt: 'LRT',
  gbpa: 'GBPA',
  ai_visibility: 'AI Visibility'
};

// Connectivity failures are not actionable and a 401 / expired session is handled by the auth layer.
const isHandledElsewhere = (cause: unknown) =>
  isNetworkApiError(cause) ||
  (isApiError(cause) && (cause.status === HTTP_STATUS.AUTHORIZATION_ERROR || isSessionExpiredError(cause)));

const isDefinedTag = (entry: [string, string | undefined]): entry is [string, string] => entry[1] !== undefined;

/** Drops the tags that have no value so Sentry never receives `undefined`. */
const compactTags = (tags: Record<string, string | undefined>) =>
  Object.fromEntries(Object.entries(tags).filter(isDefinedTag));

interface BuildReportCreateErrorReportParams {
  /** Tool whose report failed to be created */
  tool: ReportCreateTool;
  /** Sentry feature tag of the calling flow */
  feature: Feature;
  /** API-client method(s) behind the mutation */
  method: string;
  /** The mutation error, usually a `ReportCreateError` */
  error: unknown;
}

/**
 * Builds the Sentry report for a failed report-creation mutation — the `resolve` of its
 * `meta.errorReporting`. One fingerprint per tool, HTTP status and API error key, so every
 * cause is its own Sentry issue. Returns `null` for failures handled elsewhere.
 */
export const buildReportCreateErrorReport = ({ tool, feature, method, error }: BuildReportCreateErrorReportParams) => {
  const reportError = isReportCreateError(error) ? error : null;
  const cause = reportError ? reportError.cause : error;

  if (isHandledElsewhere(cause)) {
    return null;
  }

  const { httpStatus, apiErrorKey, apiErrorDetail, kind } = classifyReportCreateError(error);
  const statusSuffix = httpStatus === undefined ? '' : ` (${httpStatus})`;

  return {
    feature,
    message: `Error creating ${TOOL_LABELS[tool]} report${statusSuffix}`,
    level: LEVEL_BY_KIND[kind],
    tags: compactTags({
      tool,
      errorKind: kind,
      httpStatus: httpStatus === undefined ? undefined : String(httpStatus),
      apiErrorKey,
      step: reportError?.step
    }),
    extra: {
      method,
      ...reportError?.context,
      ...(apiErrorDetail !== undefined && { apiErrorDetail }),
      // The wrapped ApiError is not the captured exception, so its body is attached explicitly.
      ...(reportError && { apiError: cause })
    },
    fingerprint: [FINGERPRINT_PREFIX, tool, String(httpStatus ?? 'none'), apiErrorKey ?? kind]
  };
};
